import { useTranslation } from 'react-i18next';
import { ChevronRight } from './icons';
import { TransactionRow, transferToTx, swapOrderToTx, incptLedgerToTx, presalePurchaseToTx } from './TransactionRow';
import { useTransfers } from '../../hooks/useTransfers';
import { useCardTransactions } from '../../hooks/useCards';
import { useSwapOrders } from '../../hooks/useSwap';
import { useIncptTransfers } from '../../hooks/useIncptTransfers';
import { usePresale } from '../../hooks/usePresale';
import { isSwapDepositTransfer, swapDepositTxHash } from '../../lib/swapHistory';
import type { Chain, Token } from '../../lib/transfers';

export function TransactionsList({
  max,
  onSeeAll,
  light,
  filter,
}: {
  max?: number;
  onSeeAll?: () => void;
  light?: boolean;
  /** Restrict to a single chain/token (coin detail page). */
  filter?: { chain?: Chain; token?: Token };
}) {
  const { t, i18n } = useTranslation();
  const { transfers } = useTransfers(filter);
  const { txs: cardTxs } = useCardTransactions();
  const { orders: swapOrders } = useSwapOrders(filter);
  const { transfers: incptTransfers } = useIncptTransfers();
  const { purchases } = usePresale();

  const filteredTransfers = transfers.filter((tr) => !isSwapDepositTransfer(tr, swapOrders));
  // Server already scopes when filter is set; keep a defensive client pass.
  const filtered = filter
    ? filteredTransfers.filter(
        (tr) =>
          (!filter.chain || tr.chain === filter.chain) &&
          (!filter.token || tr.token === filter.token),
      )
    : filteredTransfers;

  const cryptoRows = filtered.flatMap((tr) => transferToTx(tr, i18n.language, t));

  const swapRows = swapOrders
    .filter((o) => {
      if (!filter) return true;
      const matchFrom =
        (!filter.chain || o.fromChain === filter.chain) &&
        (!filter.token || o.fromToken === filter.token);
      const matchTo =
        (!filter.chain || o.toChain === filter.chain) &&
        (!filter.token || o.toToken === filter.token);
      return matchFrom || matchTo;
    })
    .map((o) => swapOrderToTx(o, i18n.language, t, swapDepositTxHash(o, transfers)));

  // Home / global history includes off-chain INCPT; coin-detail filters exclude it.
  const incptRows = filter
    ? []
    : incptTransfers
        .filter(
          (e) =>
            e.kind === 'transfer_out' ||
            e.kind === 'transfer_in' ||
            e.kind === 'admin_grant' ||
            e.kind === 'promocode' ||
            e.kind === 'referral',
        )
        .map((e) => incptLedgerToTx(e, i18n.language, t));

  const purchaseRows = filter ? [] : purchases.map((p) => presalePurchaseToTx(p, i18n.language, t));

  // Coin-detail pages filter by chain — don't mix in card transactions there.
  const allRows = filter
    ? [...cryptoRows, ...swapRows].sort((a, b) => (b.sortTs ?? 0) - (a.sortTs ?? 0))
    : [...cryptoRows, ...swapRows, ...incptRows, ...purchaseRows, ...cardTxs].sort(
        (a, b) => (b.sortTs ?? 0) - (a.sortTs ?? 0),
      );
  const items = typeof max === 'number' ? allRows.slice(0, max) : allRows;

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-[16px] font-medium text-ink">{t('wallet.operations')}</h2>
        <button onClick={onSeeAll} className="flex items-center text-muted">
          <span className="text-[14px]">{t('wallet.all')}</span>
          <ChevronRight size={14} />
        </button>
      </div>
      {items.length === 0 ? (
        <p className="py-4 text-center text-[13px] text-muted">{t('wallet.noTransactions')}</p>
      ) : (
        <div className="flex flex-col gap-[10px]">
          {items.map((tx, i) => (
            <TransactionRow key={tx.partnerOrderId ?? `${tx.sortTs}-${i}`} tx={tx} light={light} />
          ))}
        </div>
      )}
    </section>
  );
}
